import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import MetaData from '../Layouts/MetaData';
import BackdropLoader from '../Layouts/BackdropLoader';

const REASON_LABELS = {
    order_reward: 'Points earned',
    order_redemption: 'Points redeemed',
    order_cash_payment: 'Paid with 1313 Cash',
    cash_topup: '1313 Cash top-up',
    reward_clawback: 'Points clawed back',
    admin_adjustment: 'Admin adjustment',
};

const WalletDetail = () => {
    const { userId } = useParams();
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);

    const [addCashOpen, setAddCashOpen] = useState(false);
    const [cashAmount, setCashAmount] = useState("");
    const [cashNote, setCashNote] = useState("");
    const [adding, setAdding] = useState(false);

    const fetchDetail = async () => {
        try {
            const { data } = await axios.get(`/api/v1/admin/wallet/${userId}`);
            setData(data);
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to load wallet ledger", { variant: "error" });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDetail();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId]);

    const confirmAddCash = async () => {
        const amount = Number(cashAmount);
        if (!amount || amount <= 0) {
            enqueueSnackbar("Please enter a valid amount", { variant: "error" });
            return;
        }
        setAdding(true);
        try {
            await axios.post(`/api/v1/admin/wallet/${userId}/add-cash`, { amount, note: cashNote || undefined });
            enqueueSnackbar(`HK$${amount.toLocaleString()} added to this customer's 1313 Cash`, { variant: "success" });
            setAddCashOpen(false);
            setCashAmount("");
            setCashNote("");
            fetchDetail();
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to add cash", { variant: "error" });
        } finally {
            setAdding(false);
        }
    };

    if (loading) return <BackdropLoader />;
    if (!data) return null;

    // Group transactions by the order they relate to — anything without
    // an order (top-ups, admin adjustments) falls into "General".
    const groups = [];
    const groupMap = {};
    data.transactions.forEach((txn) => {
        const key = txn.order?._id || 'general';
        if (!groupMap[key]) {
            groupMap[key] = { order: txn.order || null, transactions: [] };
            groups.push(groupMap[key]);
        }
        groupMap[key].transactions.push(txn);
    });

    return (
        <div className="flex flex-col gap-4">
            <MetaData title="Admin: Wallet Ledger | 1313 Mart" />

            <Link to="/admin/wallets" className="flex items-center gap-0 font-medium text-primary-blue uppercase w-fit">
                <ArrowBackIosIcon sx={{ fontSize: "18px" }} />Back to Wallets
            </Link>

            <div className="bg-white shadow-lg rounded-lg p-6 flex flex-col sm:flex-row justify-between gap-4">
                <div>
                    <h1 className="text-lg font-medium">{data.user.name}</h1>
                    <p className="text-sm text-gray-500">{data.user.email}</p>
                </div>
                <div className="flex items-center gap-6">
                    <div>
                        <p className="text-xs text-gray-400 uppercase">Points</p>
                        <p className="text-xl font-semibold text-primary-orange">{data.points.toLocaleString()}</p>
                    </div>
                    <div>
                        <p className="text-xs text-gray-400 uppercase">1313 Cash</p>
                        <p className="text-xl font-semibold text-primary-green">HK${data.cash.toLocaleString()}</p>
                    </div>
                    <button
                        onClick={() => setAddCashOpen(true)}
                        className="bg-primary-green px-4 py-2 text-white text-sm font-medium rounded shadow hover:shadow-lg"
                    >
                        + Add Cash
                    </button>
                </div>
            </div>

            <Dialog open={addCashOpen} onClose={() => setAddCashOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Add 1313 Cash</DialogTitle>
                <DialogContent className="flex flex-col gap-3">
                    <p className="text-sm text-gray-600">This adds real spendable 1313 Cash to <span className="font-medium">{data.user.name}</span>'s wallet — no actual payment is taken.</p>
                    <TextField
                        label="Amount (HK$)"
                        type="number"
                        size="small"
                        value={cashAmount}
                        onChange={(e) => setCashAmount(e.target.value)}
                        autoFocus
                    />
                    <TextField
                        label="Note (optional)"
                        size="small"
                        value={cashNote}
                        onChange={(e) => setCashNote(e.target.value)}
                        placeholder="e.g. Compensation for delayed delivery"
                    />
                </DialogContent>
                <DialogActions>
                    <button onClick={() => setAddCashOpen(false)} className="py-2 px-4 text-sm text-gray-600">Cancel</button>
                    <button onClick={confirmAddCash} disabled={adding} className="py-2 px-5 bg-primary-green text-white text-sm font-medium rounded shadow hover:shadow-lg disabled:opacity-60">
                        {adding ? "Adding..." : "Add Cash"}
                    </button>
                </DialogActions>
            </Dialog>

            {groups.length === 0 && (
                <div className="bg-white shadow-lg rounded-lg p-6">
                    <p className="text-sm text-gray-400">No wallet activity yet.</p>
                </div>
            )}

            {groups.map((group, i) => (
                <div key={i} className="bg-white shadow-lg rounded-lg overflow-hidden">
                    <div className="bg-gray-50 px-6 py-3 border-b">
                        <h3 className="font-medium">
                            {group.order ? `Order #${group.order.orderNumber ?? group.order._id}` : 'General (top-ups & adjustments)'}
                        </h3>
                    </div>
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-gray-500 text-xs uppercase border-b">
                                <th className="px-6 py-2">Date</th>
                                <th className="px-6 py-2">Type</th>
                                <th className="px-6 py-2">Reason</th>
                                <th className="px-6 py-2 text-right">Amount</th>
                                <th className="px-6 py-2 text-right">Balance After</th>
                            </tr>
                        </thead>
                        <tbody>
                            {group.transactions.map((txn) => (
                                <tr key={txn._id} className="border-b last:border-0">
                                    <td className="px-6 py-2.5">{new Date(txn.createdAt).toLocaleString('en-HK')}</td>
                                    <td className="px-6 py-2.5 capitalize">{txn.walletType === 'cash' ? '1313 Cash' : 'Points'}</td>
                                    <td className="px-6 py-2.5">
                                        {REASON_LABELS[txn.reason] || txn.reason}{txn.note ? ` — ${txn.note}` : ''}
                                        {txn.addedBy && <span className="text-gray-400"> (by {txn.addedBy})</span>}
                                    </td>
                                    <td className={`px-6 py-2.5 text-right font-medium ${txn.direction === 'credit' ? 'text-primary-green' : 'text-red-500'}`}>
                                        {txn.direction === 'credit' ? '+' : '-'}{txn.walletType === 'cash' ? `HK$${txn.amount.toLocaleString()}` : txn.amount.toLocaleString()}
                                    </td>
                                    <td className="px-6 py-2.5 text-right">{txn.walletType === 'cash' ? `HK$${txn.balanceAfter.toLocaleString()}` : txn.balanceAfter.toLocaleString()}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ))}
        </div>
    );
};

export default WalletDetail;